import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Container from '../../components/menu/Container'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { Toast } from 'primereact/toast'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { useGlobalState } from '../../components/globalState/GlobalStateProvider'
import {
  getSimulacaoRenovacao,
  postSimularRenovacao,
  postSalvarRenovacao,
  postPropostaRenovacao,
  SimulacaoResponse,
  PlanoSimulado,
  ServicoOverride,
  ConsumoProduto,
} from '../../service/renovacao'

const NAVY = '#183462'
const fmtBRL = (v?: number) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtPct = (v?: number) => (v == null ? '0%' : `${v.toFixed(1)}%`)

const NIVEL_LABEL: Record<string, string> = { ABAIXO: 'Plano Abaixo', ATUAL: 'Plano Atual', ACIMA: 'Plano Acima' }
const NIVEL_COLOR: Record<string, string> = { ABAIXO: '#0891b2', ATUAL: NAVY, ACIMA: '#7c3aed' }

const planoByNivel = (sim: SimulacaoResponse | null, nivel: string): PlanoSimulado | undefined => {
  if (!sim) return undefined
  if (nivel === 'ABAIXO') return sim.planoAbaixo
  if (nivel === 'ACIMA') return sim.planoAcima
  return sim.planoAtual
}

export default function RenovacaoClientePage() {
  const { idCliente } = useParams<{ idCliente: string }>()
  const navigate = useNavigate()
  const { state } = useGlobalState()
  const toast = useRef<Toast>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const [sim, setSim] = useState<SimulacaoResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [reajuste, setReajuste] = useState<number>(0)
  const [meses, setMeses] = useState<number>(12)
  const [dataInicio, setDataInicio] = useState<Date | null>(new Date())
  const [nivelSel, setNivelSel] = useState<string>('ATUAL')
  const [overrides, setOverrides] = useState<ServicoOverride[]>([])
  const [idPlanoAbaixo, setIdPlanoAbaixo] = useState<string | undefined>(undefined)
  const [idPlanoAcima, setIdPlanoAcima] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)
  const [gerando, setGerando] = useState(false)

  const podeEditar = state.user.funcoes.includes('CLIENTE_CADASTRAR')

  useEffect(() => {
    if (!idCliente) return
    setLoading(true)
    getSimulacaoRenovacao(idCliente)
      .then((s) => {
        setSim(s)
        setReajuste(Number(s.reajustePercentual) || 0)
        setMeses(s.mesesContrato || 12)
      })
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a simulação', life: 5000 }))
      .finally(() => setLoading(false))
  }, [idCliente])

  const recalcular = useCallback(
    (nextOverrides: ServicoOverride[], nextReajuste: number, nextMeses: number, nextIdAbaixo?: string, nextIdAcima?: string) => {
      if (!idCliente) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        postSimularRenovacao(idCliente, {
          reajustePercentual: nextReajuste,
          mesesContrato: nextMeses,
          overrides: nextOverrides,
          idPlanoAbaixo: nextIdAbaixo,
          idPlanoAcima: nextIdAcima,
        })
          .then(setSim)
          .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao recalcular', life: 4000 }))
      }, 450)
    },
    [idCliente]
  )

  const onReajuste = (v: number) => {
    setReajuste(v)
    recalcular(overrides, v, meses, idPlanoAbaixo, idPlanoAcima)
  }
  const onMeses = (v: number) => {
    setMeses(v)
    recalcular(overrides, reajuste, v, idPlanoAbaixo, idPlanoAcima)
  }

  const trocarPlano = (nivel: string, id: string) => {
    // ao trocar o plano-tier, descarta overrides daquele nível (serviços podem ser outros)
    const next = overrides.filter((o) => o.nivel !== nivel)
    setOverrides(next)
    const novoAbaixo = nivel === 'ABAIXO' ? id : idPlanoAbaixo
    const novoAcima = nivel === 'ACIMA' ? id : idPlanoAcima
    if (nivel === 'ABAIXO') setIdPlanoAbaixo(id)
    if (nivel === 'ACIMA') setIdPlanoAcima(id)
    recalcular(next, reajuste, meses, novoAbaixo, novoAcima)
  }

  const editarValor = (nivel: string, identificador: string, valor: number) => {
    const next = overrides.filter((o) => !(o.nivel === nivel && o.identificador === identificador))
    next.push({ nivel, identificador, valorUnitSimulado: valor })
    setOverrides(next)
    recalcular(next, reajuste, meses, idPlanoAbaixo, idPlanoAcima)
  }

  const limparOverrides = () => {
    setOverrides([])
    setReajuste(0)
    recalcular([], 0, meses, idPlanoAbaixo, idPlanoAcima)
  }

  const escolhaAtual = () => ({
    simulacao: { reajustePercentual: reajuste, mesesContrato: meses, overrides, idPlanoAbaixo, idPlanoAcima },
    nivelEscolhido: nivelSel,
    dataInicioVigencia: dataInicio,
    duracaoMeses: meses,
    destinatario: sim?.clienteResponsavel,
  })

  const salvar = () => {
    const plano = planoByNivel(sim, nivelSel)
    if (!idCliente || !plano) return
    confirmDialog({
      header: 'Confirmar renovação',
      message: `Salvar "${plano.descricao}" como plano do cliente e registrar contrato de ${meses} meses?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Salvar',
      rejectLabel: 'Cancelar',
      accept: () => {
        setSaving(true)
        postSalvarRenovacao(idCliente, escolhaAtual())
          .then((s) => {
            setSim(s)
            setReajuste(0)
            setOverrides([])
            toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Renovação salva e contrato registrado.', life: 4000 })
          })
          .catch((e) => toast.current?.show({ severity: 'error', summary: 'Erro', detail: e?.message || 'Falha ao salvar', life: 6000 }))
          .finally(() => setSaving(false))
      },
    })
  }

  const gerarProposta = () => {
    if (!idCliente) return
    setGerando(true)
    postPropostaRenovacao(idCliente, escolhaAtual())
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        const a = document.createElement('a')
        a.href = url
        a.download = 'Proposta.pdf'
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 10000)
      })
      .catch(() => toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao gerar a proposta', life: 5000 }))
      .finally(() => setGerando(false))
  }

  const planosOrdenados = (): PlanoSimulado[] =>
    [sim?.planoAbaixo, sim?.planoAtual, sim?.planoAcima].filter((p): p is PlanoSimulado => !!p)

  const chartOptions = (): Highcharts.Options | null => {
    if (!sim) return null
    const planos = planosOrdenados()
    if (!planos.length) return null
    const consumo = Number(sim.consumoMensalAtual) || 0
    return {
      chart: { type: 'column', height: 300, backgroundColor: 'transparent' },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: { categories: planos.map((p) => NIVEL_LABEL[p.nivel]) },
      yAxis: {
        title: { text: 'Projeção mensal (R$)' },
        min: 0,
        plotLines: consumo
          ? [{ value: consumo, color: '#ef4444', width: 2, dashStyle: 'Dash', label: { text: 'Consumo atual ' + fmtBRL(consumo), style: { color: '#ef4444', fontSize: '0.7rem' } }, zIndex: 5 }]
          : [],
      },
      tooltip: { valuePrefix: 'R$ ', valueDecimals: 2 },
      plotOptions: { column: { borderRadius: 4, dataLabels: { enabled: true, formatter() { return 'R$ ' + Highcharts.numberFormat(this.y || 0, 2, ',', '.') } } } },
      legend: { enabled: false },
      series: [
        {
          type: 'column',
          name: 'Projeção mensal',
          data: planos.map((p) => ({ y: Number(p.projecaoMensal) || 0, color: NIVEL_COLOR[p.nivel] })),
        },
      ],
    }
  }

  if (loading)
    return (
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: NAVY }} />
        </div>
      </Container>
    )

  if (!sim)
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
          <i className="pi pi-exclamation-circle" style={{ fontSize: '3rem' }} />
          <p style={{ marginTop: '1rem' }}>Não foi possível carregar a simulação.</p>
        </div>
      </Container>
    )

  const planoSel = planoByNivel(sim, nivelSel)
  const chart = chartOptions()

  return (
    <Container>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
            <i className="pi pi-sync" style={{ marginRight: 8, color: NAVY }} />
            Renovação de Contrato
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: '1.05rem', color: '#334155' }}>{sim.clienteNome}</span>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{sim.clienteDocumento}</span>
          </div>
        </div>
        <Button label="Voltar" icon="pi pi-arrow-left" outlined onClick={() => navigate('/cliente')} />
      </div>

      {sim.aviso && (
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', borderRadius: 10, padding: '0.9rem 1.1rem', marginBottom: '1.25rem' }}>
          <i className="pi pi-exclamation-triangle" style={{ marginRight: 8 }} />
          {sim.aviso}
        </div>
      )}

      {/* Resumo financeiro + consumo */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <MiniKpi label="Consumo mensal atual" value={fmtBRL(sim.consumoMensalAtual)} icon="pi pi-wallet" from="#0369a1" to="#0891b2" />
        <MiniKpi label="Franquia atual (VCM)" value={fmtBRL(sim.vcmAtual)} icon="pi pi-tag" from="#4f46e5" to="#6366f1" />
        {sim.saude && <MiniKpi label="Score financeiro" value={`${sim.saude.scoreFinanceiro}/100`} icon="pi pi-gauge" from="#065f46" to="#10b981" />}
        {sim.saude && <MiniKpi label="Pontualidade" value={`${sim.saude.percentualPontualidade}%`} icon="pi pi-check-circle" from="#166534" to="#22c55e" />}
        {sim.saude && <MiniKpi label="Inadimplência" value={`${sim.saude.percentualInadimplencia}%`} icon="pi pi-exclamation-triangle" from="#b91c1c" to="#dc2626" />}
      </div>

      {/* Podium de consumo */}
      {sim.podiumConsumo && sim.podiumConsumo.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-trophy" label="Top produtos mais consumidos — últimos 12 meses" />
          <Podium itens={sim.podiumConsumo} />
        </div>
      )}

      {/* Controles de simulação */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>Reajuste (%)</label>
          <InputNumber value={reajuste} onValueChange={(e) => onReajuste(e.value || 0)} suffix=" %" minFractionDigits={0} maxFractionDigits={2} showButtons step={1} style={{ width: 130 }} inputStyle={{ width: '100%', minWidth: 0 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>Duração do contrato</label>
          <Dropdown value={meses} onChange={(e) => onMeses(e.value)} options={[{ label: '12 meses', value: 12 }, { label: '24 meses', value: 24 }]} style={{ width: 150 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>Início da vigência</label>
          <Calendar value={dataInicio} onChange={(e) => setDataInicio(e.value as Date)} dateFormat="dd/mm/yy" showIcon style={{ width: 170 }} />
        </div>
        <Button label="Limpar simulação" icon="pi pi-undo" text onClick={limparOverrides} />
      </div>

      {/* 3 cards comparativos */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'stretch' }}>
        {planosOrdenados().map((p) => {
          const selecionado = p.nivel === nivelSel
          const diff = Number(p.diferencaMensalVsConsumoAtual) || 0
          return (
            <div
              key={p.nivel}
              onClick={() => setNivelSel(p.nivel)}
              style={{
                flex: 1,
                minWidth: 240,
                cursor: 'pointer',
                background: '#fff',
                borderRadius: 16,
                padding: '1.25rem',
                boxShadow: selecionado ? `0 0 0 3px ${NIVEL_COLOR[p.nivel]}, 0 10px 24px rgba(0,0,0,0.12)` : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'box-shadow .15s, transform .15s',
                transform: selecionado ? 'translateY(-2px)' : 'none',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Tag value={NIVEL_LABEL[p.nivel]} style={{ background: NIVEL_COLOR[p.nivel] }} />
                {selecionado && <i className="pi pi-check-circle" style={{ color: NIVEL_COLOR[p.nivel], fontSize: '1.2rem' }} />}
              </div>
              <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', minHeight: 40 }}>{p.descricao}</div>
              <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: 2 }}>Franquia {fmtBRL(p.vcm)}</div>

              {p.nivel !== 'ATUAL' && (() => {
                const opcoes = p.nivel === 'ABAIXO' ? (sim.opcoesAbaixo || []) : (sim.opcoesAcima || [])
                if (opcoes.length <= 1) return null
                return (
                  <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ display: 'block', fontSize: '0.68rem', color: '#64748b', fontWeight: 600, marginBottom: 3 }}>Trocar plano</label>
                    <Dropdown
                      value={p.idPlano}
                      options={opcoes.map((o) => ({ label: `${o.descricao} — ${fmtBRL(o.vcm)}`, value: o.idPlano }))}
                      onChange={(e) => trocarPlano(p.nivel, e.value)}
                      style={{ width: '100%' }}
                      panelStyle={{ fontSize: '0.82rem' }}
                    />
                  </div>
                )
              })()}

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projeção mensal</div>
                <div style={{ fontSize: '1.7rem', fontWeight: 800, color: NIVEL_COLOR[p.nivel], lineHeight: 1.1 }}>{fmtBRL(p.projecaoMensal)}</div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>{fmtBRL(p.projecaoTrimestral)} / trimestre</div>
              </div>

              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>vs. consumo atual</span>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: diff > 0 ? '#dc2626' : '#16a34a' }}>
                  {diff > 0 ? '+' : ''}{fmtBRL(diff)} ({fmtPct(p.percentualVsConsumoAtual)})
                </span>
              </div>
              {Number(p.economiaDescontosTrimestral) > 0 && (
                <div style={{ marginTop: 6, fontSize: '0.74rem', color: '#16a34a' }}>
                  <i className="pi pi-arrow-down" style={{ marginRight: 4 }} />
                  Economia de {fmtBRL(p.economiaDescontosTrimestral)}/trimestre em descontos
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Gráfico comparativo */}
      {chart && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-chart-bar" label="Comparativo de projeção mensal" />
          <HighchartsReact highcharts={Highcharts} options={chart} />
        </div>
      )}

      {/* Tabela editável do plano selecionado */}
      {planoSel && (
        <div style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <SectionTitle icon="pi pi-sliders-h" label={`Serviços — ${NIVEL_LABEL[planoSel.nivel]} (${planoSel.descricao})`} />
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: -6, marginBottom: 12 }}>
            Edite o valor unitário simulado de qualquer serviço para recalcular a projeção em tempo real.
          </p>
          <DataTable value={planoSel.servicos} size="small" stripedRows emptyMessage="Plano sem serviços">
            <Column field="descricaoServico" header="Produto" />
            <Column header="Qtd. trim." body={(s) => s.quantidadeTrimestral} align="center" alignHeader="center" />
            <Column header="Unit. contratado" body={(s) => fmtBRL(s.valorUnitContratado)} align="right" alignHeader="right" />
            <Column header="Unit. atual" body={(s) => fmtBRL(s.valorUnitAtual)} align="right" alignHeader="right" />
            <Column
              header="Unit. simulado"
              align="right"
              alignHeader="right"
              body={(s) =>
                podeEditar ? (
                  <InputNumber
                    value={s.valorUnitSimulado}
                    onValueChange={(e) => editarValor(planoSel.nivel, s.identificador, e.value || 0)}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                    inputStyle={{ width: 110, textAlign: 'right', ...(s.editado ? { color: '#dc2626', fontWeight: 700 } : {}) }}
                  />
                ) : (
                  fmtBRL(s.valorUnitSimulado)
                )
              }
            />
            <Column header="Custo mensal" body={(s) => <strong>{fmtBRL(s.custoMensal)}</strong>} align="right" alignHeader="right" />
          </DataTable>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.5rem', marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Projeção mensal</div>
              <div style={{ fontWeight: 800, color: NAVY, fontSize: '1.2rem' }}>{fmtBRL(planoSel.projecaoMensal)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Projeção trimestral</div>
              <div style={{ fontWeight: 800, color: NAVY, fontSize: '1.2rem' }}>{fmtBRL(planoSel.projecaoTrimestral)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Ações */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
        <Button label="Gerar Proposta.pdf" icon="pi pi-file-pdf" severity="warning" outlined loading={gerando} onClick={gerarProposta} />
        {podeEditar && (
          <Button label="Salvar plano escolhido" icon="pi pi-save" loading={saving} onClick={salvar} style={{ backgroundColor: NAVY, borderColor: NAVY }} />
        )}
      </div>
    </Container>
  )
}

const MiniKpi = ({ label, value, icon, from, to }: { label: string; value: string; icon: string; from: string; to: string }) => (
  <div style={{ background: `linear-gradient(135deg,${from},${to})`, borderRadius: 12, padding: '0.9rem 1.1rem', flex: 1, minWidth: 150, boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
        <div style={{ color: '#fff', fontSize: '1.3rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
      </div>
      <i className={icon} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.2rem' }} />
    </div>
  </div>
)

const SectionTitle = ({ icon, label }: { icon: string; label: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
    <div style={{ width: 4, height: 20, borderRadius: 2, background: NAVY }} />
    <i className={icon} style={{ color: NAVY }} />
    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{label}</span>
  </div>
)

const MEDALHAS = [
  { grad: 'linear-gradient(135deg,#b8860b,#fcd34d)', medal: '🥇', altura: 150, label: '1º' },
  { grad: 'linear-gradient(135deg,#6b7280,#cbd5e1)', medal: '🥈', altura: 120, label: '2º' },
  { grad: 'linear-gradient(135deg,#92400e,#d97706)', medal: '🥉', altura: 100, label: '3º' },
]

const Podium = ({ itens }: { itens: ConsumoProduto[] }) => {
  const top3 = itens.slice(0, 3)
  const resto = itens.slice(3, 8)
  // ordem visual do podium: 2º, 1º, 3º
  const ordemVisual = [top3[1], top3[0], top3[2]].filter(Boolean) as ConsumoProduto[]
  const idxMedalha = (p: ConsumoProduto) => top3.findIndex((t) => t === p)

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', justifyContent: 'center', flexWrap: 'wrap' }}>
        {ordemVisual.map((p) => {
          const i = idxMedalha(p)
          const m = MEDALHAS[i]
          return (
            <div key={p.identificador} style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150 }}>
              <div style={{ textAlign: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: '1.4rem' }}>{m.medal}</div>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.82rem', lineHeight: 1.15, minHeight: 32 }}>{p.descricaoProduto}</div>
              </div>
              <div style={{ background: m.grad, borderRadius: '12px 12px 0 0', height: m.altura, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 6px 16px rgba(0,0,0,0.15)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{m.label}</div>
                <div style={{ fontSize: '1rem', fontWeight: 800 }}>{fmtBRL(p.valor12Meses)}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>{p.quantidade12Meses} consultas</div>
              </div>
            </div>
          )
        })}
      </div>

      {resto.length > 0 && (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {resto.map((p, i) => (
            <div key={p.identificador} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 700, color: '#94a3b8', width: 24 }}>{i + 4}º</span>
                <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>{p.descricaoProduto}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{p.quantidade12Meses} consultas</span>
                <span style={{ fontWeight: 700, color: NAVY, fontSize: '0.88rem' }}>{fmtBRL(p.valor12Meses)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
